import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, ArrowRight, Loader2, Check, X, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const AMOUNTS = [25, 50, 75, 100];
const MAX_POLLS = 15;

export default function GiftCard() {
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ purchaser_email: "", recipient_email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const value = custom ? parseFloat(custom) : amount;

  const buy = async () => {
    if (!form.purchaser_email) { toast.error("Enter your email so we can send the receipt."); return; }
    if (!value || value < 5 || value > 500) { toast.error("Amount must be between $5 and $500."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/gift-cards/checkout", {
        amount: value,
        purchaser_email: form.purchaser_email,
        recipient_email: form.recipient_email || null,
        message: form.message,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 lg:px-12" data-testid="gift-card-page">
      <div className="max-w-2xl mx-auto">
        <p className="overline text-xs text-[#d4af37] mb-4 flex items-center gap-3"><span className="w-8 h-px bg-[#d4af37]" /> The Perfect Gift</p>
        <h1 className="font-display text-4xl lg:text-6xl text-white mb-3">Gift <span className="italic text-[#d4af37]">a fresh cut</span></h1>
        <p className="text-white/50 font-body mb-12 max-w-lg">Give the gift of a sharp look. Digital gift cards are emailed instantly and never expire — redeemable online or in the shop.</p>

        <div className="border border-white/10 bg-[#0a0a0a] p-8">
          <p className="overline text-xs text-[#d4af37] mb-4">Choose an amount</p>
          <div className="grid grid-cols-4 gap-3 mb-4">
            {AMOUNTS.map((a) => (
              <button
                key={a}
                onClick={() => { setAmount(a); setCustom(""); }}
                className={`py-5 font-display text-2xl border transition-colors duration-300 ${!custom && amount === a ? "bg-[#d4af37] border-[#d4af37] text-black" : "border-white/15 text-white hover:border-[#d4af37]"}`}
                data-testid={`gift-amount-${a}`}
              >
                ${a}
              </button>
            ))}
          </div>
          <div className="mb-8">
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Or a custom amount ($5–$500)</label>
            <input
              type="number" min="5" max="500" value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Custom amount"
              className="w-full bg-transparent border-b border-white/20 py-3 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors"
              data-testid="gift-custom-amount"
            />
          </div>

          <p className="overline text-xs text-[#d4af37] mb-4">Details</p>
          <div className="space-y-6 mb-8">
            <Field label="Your Email *" type="email" value={form.purchaser_email} onChange={(v) => setForm({ ...form, purchaser_email: v })} testid="gift-purchaser-email" />
            <Field label="Recipient Email (optional — sends the card to them)" type="email" value={form.recipient_email} onChange={(v) => setForm({ ...form, recipient_email: v })} testid="gift-recipient-email" />
            <Field label="Message (optional)" value={form.message} onChange={(v) => setForm({ ...form, message: v })} testid="gift-message" />
          </div>

          <button
            onClick={buy}
            disabled={loading}
            className="group w-full flex items-center justify-center gap-3 bg-[#d4af37] text-black py-4 overline text-xs disabled:opacity-40 hover:bg-[#c5a028] transition-colors"
            data-testid="gift-buy-btn"
          >
            {loading ? "Redirecting to checkout…" : `Buy $${value || 0} Gift Card`}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="text-white/30 text-xs font-body mt-4 text-center">Secure checkout via Stripe · Card, Apple Pay, Google Pay</p>
        </div>
      </div>
    </div>
  );
}

export function GiftCardSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [data, setData] = useState(null);
  const pollRef = useRef(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/gift-cards/session/${sessionId}`);
        if (cancelled) return;
        if (data.fulfilled && data.code) { setData(data); setState("done"); return; }
        pollRef.current += 1;
        if (pollRef.current >= MAX_POLLS) { setState("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const copyCode = () => {
    navigator.clipboard.writeText(data.code).then(() => {
      setCopied(true);
      toast.success("Code copied!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 flex items-center justify-center" data-testid="gift-success-page">
      <div className="max-w-md w-full text-center">
        {state === "checking" && (<><Loader2 className="w-10 h-10 text-[#d4af37] animate-spin mx-auto mb-6" /><p className="overline text-xs text-white/60">Confirming your purchase…</p></>)}

        {state === "done" && data && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <div className="w-20 h-20 border-2 border-[#d4af37] rounded-full flex items-center justify-center mx-auto mb-8"><Gift className="w-9 h-9 text-[#d4af37]" /></div>
            <h1 className="font-display text-4xl text-white mb-3">Gift card <span className="italic text-[#d4af37]">ready!</span></h1>
            <p className="text-white/50 font-body mb-8">A ${data.amount?.toFixed?.(0)} gift card was emailed to {data.recipient_email}. Here&apos;s the code:</p>
            <div className="border-2 border-dashed border-[#d4af37]/50 p-6 mb-8">
              <p className="overline text-[0.55rem] text-white/40 mb-2">Redemption Code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="font-display text-3xl text-[#d4af37] tracking-widest" data-testid="gift-code">{data.code}</span>
                <button onClick={copyCode} className="border border-white/15 text-white/70 p-2 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors" data-testid="copy-gift-code">
                  {copied ? <Check className="w-4 h-4 text-[#d4af37]" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4">
              <Link to="/" className="border border-white/20 px-8 py-3.5 overline text-xs text-white hover:border-[#d4af37] hover:text-[#d4af37] transition-colors">Back Home</Link>
              <Link to="/book" className="bg-[#d4af37] text-black px-8 py-3.5 overline text-xs hover:bg-[#c5a028] transition-colors">Book a Cut</Link>
            </div>
          </motion.div>
        )}

        {state === "timeout" && (<><Loader2 className="w-10 h-10 text-[#d4af37] mx-auto mb-6" /><h1 className="font-display text-3xl text-white mb-3">Almost there</h1><p className="text-white/50 font-body mb-8">Your gift card is processing — the code will arrive by email shortly.</p><Link to="/" className="bg-[#d4af37] text-black px-8 py-3.5 overline text-xs">Back Home</Link></>)}

        {state === "error" && (<><div className="w-16 h-16 border-2 border-red-400/50 rounded-full flex items-center justify-center mx-auto mb-6"><X className="w-8 h-8 text-red-400" /></div><h1 className="font-display text-3xl text-white mb-3">Something went wrong</h1><p className="text-white/50 font-body mb-8">We couldn&apos;t confirm this purchase. If you were charged, the code will still be emailed to you.</p><Link to="/gift-card" className="border border-[#d4af37] text-[#d4af37] px-8 py-3.5 overline text-xs hover:bg-[#d4af37] hover:text-black transition-colors">Try Again</Link></>)}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="overline text-[0.6rem] text-white/50 block mb-2">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b border-white/20 py-3 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors" data-testid={testid} />
    </div>
  );
}
